// @ts-nocheck
/**
 * UltimatePicker
 * A self-contained location picker with:
 *   - Leaflet map (Google hybrid/street tiles, no API key needed for the map)
 *   - Google Street View (requires VITE_GOOGLE_MAPS_API_KEY)
 *   - Google Places Autocomplete search (requires VITE_GOOGLE_MAPS_API_KEY)
 *   - Save/lock/clear flow with free Street View thumbnail preview
 *
 * Props:
 *   value              {string}   "lat, lng" coordinate string
 *   onChange           {fn}       called with "lat, lng" string
 *   linkValue          {string}   Google Maps link
 *   onLinkChange       {fn}       called with maps link
 *   streetViewValue    {string}   full Google Street View URL
 *   onStreetViewChange {fn}       called with street view URL
 *   thumbnailValue     {string}   thumbnail URL (free, from streetviewpixels)
 *   onThumbnailChange  {fn}       called with thumbnail URL when saved
 *   googleApiKey       {string}   override for Google Maps API key (optional, defaults to VITE_GOOGLE_MAPS_API_KEY)
 *
 * Dependencies: leaflet, react-leaflet (v4), leaflet/dist/leaflet.css
 * Install: npm install leaflet react-leaflet @types/leaflet --save --legacy-peer-deps
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default marker icon (Vite/webpack asset issue)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Ensure Google Places dropdown renders above everything
if (typeof document !== "undefined" && !document.getElementById("ultimate-picker-pac-style")) {
  const style = document.createElement("style");
  style.id = "ultimate-picker-pac-style";
  style.textContent = ".pac-container { z-index: 10000 !important; }";
  document.head.appendChild(style);
}

const TILE_HYBRID = "https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}";
const TILE_STREET = "https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}";

// ─── Google Maps JS API loader (singleton) ────────────────────────────────────
let _googleLoaded = false;
let _googlePromise = null;
function loadGoogleMaps(apiKey) {
  if (_googleLoaded && window.google?.maps) return Promise.resolve();
  if (_googlePromise) return _googlePromise;
  _googlePromise = new Promise((resolve) => {
    if (window.google?.maps) { _googleLoaded = true; resolve(); return; }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.onload = () => { _googleLoaded = true; resolve(); };
    document.head.appendChild(script);
  });
  return _googlePromise;
}

// ─── URL builders ─────────────────────────────────────────────────────────────
function buildStreetViewUrl({ lat, lng, heading, pitch, zoom, panoId }) {
  const fov = Math.max(1, Math.min(90, 75 / Math.pow(2, zoom || 0)));
  const h = ((heading || 0) % 360).toFixed(1);
  const t = (90 + (pitch || 0)).toFixed(2);
  const panoParam = panoId ? `!1s${panoId}` : "";
  const thumbPitch = (-(pitch || 0)).toFixed(6);
  const thumbYaw = ((heading || 0) % 360).toFixed(6);
  const thumbParam = panoId
    ? `!6shttps:%2F%2Fstreetviewpixels-pa.googleapis.com%2Fv1%2Fthumbnail%3Fcb_client%3Dmaps_sv.tactile%26w%3D900%26h%3D600%26pitch%3D${thumbPitch}%26panoid%3D${panoId}%26yaw%3D${thumbYaw}`
    : "";
  return `https://www.google.com/maps/@${lat},${lng},3a,${fov.toFixed(1)}y,${h}h,${t}t/data=!3m7!1e1!3m5${panoParam}!2e0${thumbParam}!7i16384!8i8192?entry=ttu`;
}

function buildThumbnailUrl({ pitch, panoId, heading }) {
  if (!panoId) return "";
  const p = (-(pitch || 0)).toFixed(2);
  const y = ((heading || 0) % 360).toFixed(2);
  return `https://streetviewpixels-pa.googleapis.com/v1/thumbnail?cb_client=maps_sv.tactile&w=600&h=300&pitch=${p}&panoid=${panoId}&yaw=${y}`;
}

// ─── Internal sub-components ──────────────────────────────────────────────────
function ClickHandler({ onClick }) {
  useMapEvents({ click: (e) => onClick(e.latlng) });
  return null;
}

function FlyTo({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, 18);
  }, [center, map]);
  return null;
}

function SearchBox({ onSelect, apiKey }) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    loadGoogleMaps(apiKey).then(() => {
      if (!inputRef.current || autocompleteRef.current) return;
      const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: "br" },
        fields: ["geometry"],
      });
      ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        if (place?.geometry?.location) {
          onSelect({
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          });
        }
      });
      autocompleteRef.current = ac;
    });
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      const v = inputRef.current?.value || "";
      const coordMatch = v.match(/^(-?\d+[.,]\d+)\s*[,;\s]\s*(-?\d+[.,]\d+)$/);
      if (coordMatch) {
        e.preventDefault();
        const lat = parseFloat(coordMatch[1].replace(",", "."));
        const lng = parseFloat(coordMatch[2].replace(",", "."));
        if (!isNaN(lat) && !isNaN(lng)) onSelect({ lat, lng });
      }
    }
  };

  return (
    <div style={{ position: "absolute", top: 10, left: 50, zIndex: 1000, width: "calc(100% - 60px)", maxWidth: 400 }}>
      <input
        ref={inputRef}
        type="text"
        onKeyDown={handleKeyDown}
        placeholder="Pesquisar endereço..."
        style={{
          width: "100%",
          padding: "8px 12px",
          border: "2px solid #3b82f6",
          borderRadius: 8,
          fontSize: 14,
          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
          outline: "none",
          background: "#fff",
        }}
      />
    </div>
  );
}

function StreetViewPanel({ position, svActive, onActivate, onPovChange, apiKey }) {
  const containerRef = useRef(null);
  const svRef = useRef(null);

  useEffect(() => {
    if (!svActive || !position || !containerRef.current) return;

    loadGoogleMaps(apiKey).then(() => {
      if (!svRef.current) {
        svRef.current = new window.google.maps.StreetViewPanorama(containerRef.current, {
          position: { lat: position.lat, lng: position.lng },
          pov: { heading: 0, pitch: 0 },
          zoom: 1,
          addressControl: false,
          fullscreenControl: true,
          motionTracking: false,
          motionTrackingControl: false,
        });

        const notify = () => {
          if (!onPovChange || !svRef.current) return;
          const pov = svRef.current.getPov();
          const pos = svRef.current.getPosition();
          const panoId = svRef.current.getPano();
          onPovChange({
            lat: pos?.lat() || position.lat,
            lng: pos?.lng() || position.lng,
            heading: pov.heading,
            pitch: pov.pitch,
            zoom: svRef.current.getZoom(),
            panoId,
          });
        };

        svRef.current.addListener("pov_changed", notify);
        svRef.current.addListener("position_changed", notify);
      } else {
        svRef.current.setPosition({ lat: position.lat, lng: position.lng });
      }
    });
  }, [position, svActive]);

  if (!position) {
    return (
      <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#f3f4f6", color: "#9ca3af", fontSize: 14, borderRadius: 12, border: "2px solid #d1d5db" }}>
        Selecione um ponto no mapa
      </div>
    );
  }

  if (!svActive) {
    return (
      <div
        onClick={onActivate}
        style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#f3f4f6", color: "#3b82f6", fontSize: 14, borderRadius: 12, border: "2px dashed #3b82f6", cursor: "pointer", gap: 8 }}
      >
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" /><path d="M12 8v8M8 12h8" />
        </svg>
        <span style={{ fontWeight: 600 }}>Abrir Street View</span>
        <span style={{ fontSize: 12, color: "#6b7280" }}>Clique para carregar</span>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ width: "100%", height: "100%", borderRadius: 12, border: "2px solid #d1d5db", overflow: "hidden" }} />
  );
}

function SavedView({ coordenadas, linkMaps, linkStreetView, thumbnailUrl, onClear }) {
  return (
    <div style={{ border: "2px solid #22c55e", borderRadius: 12, padding: 16, background: "#f0fdf4" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <span style={{ fontWeight: 600, color: "#16a34a", fontSize: 14 }}>Localização salva</span>
        <button
          type="button"
          onClick={onClear}
          style={{ padding: "4px 12px", background: "#ef4444", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
        >
          Limpar e refazer
        </button>
      </div>

      {thumbnailUrl && (
        <div style={{ marginBottom: 12, borderRadius: 8, overflow: "hidden", border: "1px solid #d1d5db" }}>
          <img src={thumbnailUrl} alt="Street View" style={{ width: "100%", height: 200, objectFit: "cover", display: "block" }} />
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        <div>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 2 }}>Coordenadas</div>
          <div style={{ fontSize: 13, fontWeight: 500, wordBreak: "break-all" }}>{coordenadas || "—"}</div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 2 }}>Link Maps</div>
          {linkMaps
            ? <a href={linkMaps} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: "#2563eb", wordBreak: "break-all", display: "block" }}>Abrir no Maps</a>
            : <span style={{ fontSize: 13, color: "#9ca3af" }}>—</span>}
        </div>
        <div>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 2 }}>Street View</div>
          {linkStreetView
            ? <a href={linkStreetView} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: "#2563eb", display: "block" }}>Abrir Street View</a>
            : <span style={{ fontSize: 13, color: "#9ca3af" }}>Sem Street View</span>}
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function UltimatePicker({
  value,
  onChange,
  linkValue,
  onLinkChange,
  streetViewValue,
  onStreetViewChange,
  thumbnailValue,
  onThumbnailChange,
  googleApiKey,
}) {
  const apiKey = googleApiKey || (typeof import.meta !== "undefined" ? import.meta.env?.VITE_GOOGLE_MAPS_API_KEY : "") || "";

  const [position, setPosition] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [satellite, setSatellite] = useState(true);
  const [streetViewLink, setStreetViewLink] = useState(streetViewValue || "");
  const [svActive, setSvActive] = useState(false);
  const [lastPov, setLastPov] = useState(null);
  const [saved, setSaved] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState(thumbnailValue || "");

  // Restore saved state when value already set (e.g. from DB)
  useEffect(() => {
    if (value) {
      const parts = value.split(",").map((s) => parseFloat(s.trim()));
      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        setPosition({ lat: parts[0], lng: parts[1] });
      }
    }
    if (thumbnailValue) {
      setThumbnailUrl(thumbnailValue);
      setSaved(true);
    }
  }, []);

  useEffect(() => {
    if (onStreetViewChange) onStreetViewChange(streetViewLink);
  }, [streetViewLink]);

  const handleClick = (latlng) => {
    if (saved) return;
    const coord = `${latlng.lat.toFixed(7)}, ${latlng.lng.toFixed(7)}`;
    setPosition(latlng);
    onChange(coord);
    if (onLinkChange) onLinkChange(`https://www.google.com/maps?q=${latlng.lat.toFixed(7)},${latlng.lng.toFixed(7)}`);
  };

  const handleSearch = useCallback((latlng) => {
    if (saved) return;
    setFlyTarget(latlng);
    const coord = `${latlng.lat.toFixed(7)}, ${latlng.lng.toFixed(7)}`;
    setPosition(latlng);
    onChange(coord);
    if (onLinkChange) onLinkChange(`https://www.google.com/maps?q=${latlng.lat.toFixed(7)},${latlng.lng.toFixed(7)}`);
  }, [onChange, onLinkChange, saved]);

  const handlePovChange = useCallback((pov) => {
    setLastPov(pov);
    setStreetViewLink(buildStreetViewUrl(pov));
  }, []);

  const handleSave = () => {
    const thumb = lastPov ? buildThumbnailUrl(lastPov) : "";
    setThumbnailUrl(thumb);
    setSaved(true);
    if (onThumbnailChange) onThumbnailChange(thumb);
  };

  const handleClear = () => {
    setSaved(false);
    setThumbnailUrl("");
    setSvActive(false);
    setLastPov(null);
    setPosition(null);
    setStreetViewLink("");
    onChange("");
    if (onLinkChange) onLinkChange("");
    if (onStreetViewChange) onStreetViewChange("");
    if (onThumbnailChange) onThumbnailChange("");
  };

  const defaultCenter = position || { lat: -19.47, lng: -42.53 };

  if (saved) {
    return (
      <SavedView
        coordenadas={value}
        linkMaps={linkValue}
        linkStreetView={streetViewLink || streetViewValue}
        thumbnailUrl={thumbnailUrl}
        onClear={handleClear}
      />
    );
  }

  const canSave = !!position;

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, height: 400 }}>
        {/* Map */}
        <div style={{ position: "relative", borderRadius: 12, overflow: "hidden", border: "2px solid #d1d5db" }}>
          <MapContainer center={defaultCenter} zoom={position ? 18 : 13} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <TileLayer url={satellite ? TILE_HYBRID : TILE_STREET} maxZoom={21} />
            <ClickHandler onClick={handleClick} />
            {flyTarget && <FlyTo center={flyTarget} />}
            {position && <Marker position={position} />}
          </MapContainer>
          <SearchBox onSelect={handleSearch} apiKey={apiKey} />
          <button
            type="button"
            onClick={() => setSatellite(!satellite)}
            style={{ position: "absolute", bottom: 10, right: 10, zIndex: 1000, padding: "6px 12px", background: satellite ? "#fff" : "#1e40af", color: satellite ? "#1e40af" : "#fff", border: "2px solid #1e40af", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
          >
            {satellite ? "Mapa" : "Satélite"}
          </button>
        </div>

        {/* Street View */}
        <StreetViewPanel position={position} svActive={svActive} onActivate={() => setSvActive(true)} onPovChange={handlePovChange} apiKey={apiKey} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 12 }}>
        <div>
          <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Coordenadas</label>
          <input
            type="text"
            placeholder="Ex: -23.550520, -46.633308"
            style={{ width: "100%", borderRadius: 6, border: "1px solid #d1d5db", padding: "8px", fontSize: 13 }}
            value={value || ""}
            onChange={(e) => {
              onChange(e.target.value);
              const parts = e.target.value.split(",").map((s) => parseFloat(s.trim()));
              if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
                const latlng = { lat: parts[0], lng: parts[1] };
                setPosition(latlng);
                setFlyTarget(latlng);
                if (onLinkChange) onLinkChange(`https://www.google.com/maps?q=${parts[0]},${parts[1]}`);
              }
            }}
          />
        </div>
        <div>
          <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Link Google Maps</label>
          <input
            type="url"
            placeholder="Gerado automaticamente"
            style={{ width: "100%", borderRadius: 6, border: "1px solid #d1d5db", padding: "8px", fontSize: 13, background: "#f9fafb" }}
            value={linkValue || ""}
            onChange={(e) => onLinkChange && onLinkChange(e.target.value)}
          />
        </div>
        <div>
          <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Link Street View</label>
          <input
            type="text"
            readOnly
            style={{ width: "100%", borderRadius: 6, border: "1px solid #d1d5db", padding: "8px", fontSize: 13, background: "#f9fafb", cursor: streetViewLink ? "pointer" : "default" }}
            value={streetViewLink}
            onClick={() => streetViewLink && navigator.clipboard.writeText(streetViewLink)}
            placeholder="Abra o Street View para gerar"
            title="Clique para copiar"
          />
        </div>
      </div>

      <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          disabled={!canSave}
          onClick={handleSave}
          style={{ padding: "8px 24px", background: canSave ? "#22c55e" : "#d1d5db", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: canSave ? "pointer" : "not-allowed" }}
        >
          Salvar coordenadas e links
        </button>
      </div>
    </div>
  );
}
